export enum DiaryItemType { Event = 1, Task = 2 }
export enum DiaryItemStatus { All = 0, Planned = 1, Completed = 2, Cancelled = 3 }
export enum DiaryCategory { All = 0, General = 1, Insurance = 10, Payment = 20, Valuation = 30, Shipping = 40, Exhibition = 50, Conservation = 60 }
export enum DiaryVisibility { Private = 1, ClientWide = 2 }
export enum ReminderChannel { InApp = 1, Email = 2 }
export enum LinkedEntityType {
    Artwork = 1,
    Portfolio = 2,
}
export enum DiaryCreatedSource {
    User = 1,
    System = 2,
    Import = 3,
}

export const DiaryCategoryLabels: Record<DiaryCategory, string> = {
    [DiaryCategory.All]: 'All',
    [DiaryCategory.General]: 'General',
    [DiaryCategory.Insurance]: 'Insurance',
    [DiaryCategory.Payment]: 'Payment',
    [DiaryCategory.Valuation]: 'Valuation',
    [DiaryCategory.Shipping]: 'Shipping',
    [DiaryCategory.Exhibition]: 'Exhibition',
    [DiaryCategory.Conservation]: 'Conservation'
};

export const DiaryStatusLabels: Record<DiaryItemStatus, string> = {
    [DiaryItemStatus.All]: 'All',
    [DiaryItemStatus.Planned]: 'Planned',
    [DiaryItemStatus.Completed]: 'Completed',
    [DiaryItemStatus.Cancelled]: 'Cancelled',
};

// Helper function to get category label from value
export const getCategoryLabel = (value: DiaryCategory | number | string | undefined): string => {

    if (value === undefined || value === null || value === '') {
        return 'All';
    }

    const numValue = typeof value === 'string' ? parseInt(value) : value;

    return DiaryCategoryLabels[numValue as DiaryCategory] || 'Unknown';
};

// Helper function to get category value from label (for reverse lookup)
export const getCategoryValue = (label: string): DiaryCategory | undefined => {
    const entry = Object.entries(DiaryCategoryLabels).find(([, catLabel]) => catLabel === label);
    return entry ? parseInt(entry[0]) as DiaryCategory : undefined;
};

// Helper function to get status label from value
export const getStatusLabel = (value: DiaryItemStatus | number | string | undefined): string => {

    if (value === undefined || value === null || value === '') {
        return 'All';
    }

    const numValue = typeof value === 'string' ? parseInt(value) : value;
    return DiaryStatusLabels[numValue as DiaryItemStatus] || 'Unknown';
};

// Helper function to get status value from label (for reverse lookup)
export const getStatusValue = (label: string): DiaryItemStatus | undefined => {
    const entry = Object.entries(DiaryStatusLabels).find(([, catLabel]) => catLabel === label);
    return entry ? parseInt(entry[0]) as DiaryItemStatus : undefined;
};

export const getCategoryOptions = () => {
    return Object.entries(DiaryCategoryLabels).map(([value, label]) => ({
        value: parseInt(value) as DiaryCategory,
        label
    }));
};

export const getStatusOptions = () => {
    return Object.entries(DiaryStatusLabels).map(([value, label]) => ({
        value: parseInt(value) as DiaryItemStatus,
        label
    }));
};
export interface DiaryReminderDto {
    offsetMinutes: number;
    channel: ReminderChannel;
    targetUserId?: number | null; // null => creator
    isEnabled: boolean;
}

export interface DiaryShareDto {
    userId: number;
    accessLevel: 1 | 2; // View/Edit
}

export interface DiaryLinkDto {
    entityType: number;
    entityId: string;
    label?: string | null;
    amount?: number | null;
    currency?: string | null;
    reference?: string | null;
}

export interface DiaryItemUpsertDto {
    title: string;
    description?: string | null;
    type: DiaryItemType;
    category: DiaryCategory;
    visibility: DiaryVisibility;
    startUtc?: string | null;
    endUtc?: string | null;
    dueUtc?: string | null;
    allDay: boolean;
    timezoneId?: string | null;

    recurrenceRule?: string | null;
    parentDiaryItemId?: number | null;

    shares: DiaryShareDto[];
    reminders: DiaryReminderDto[];
    links: DiaryLinkDto[];
}

export interface DiaryItemDto extends DiaryItemUpsertDto {
    id: number;
    clientId: number;

    status: DiaryItemStatus;
    createdByUserId?: number | null;
    createdSource: DiaryCreatedSource;

    createdUtc: string;
    updatedUtc: string;

    startUtcStr: string;
    endUtcStr: string;
    dueUtcStr: string;

    children: DiaryItemDto[]; // manual + generated
}

export interface DiaryItemUpsertDtoEx extends DiaryItemUpsertDto {
    startUtcStr: string;
    endUtcStr: string;
    dueUtcStr: string;
}

export interface DiaryItem extends DiaryItemUpsertDto {
    id: number;
    clientId: number;
    status: DiaryItemStatus;
    createdByUserId?: number | null;
    createdSource: DiaryCreatedSource;
    createdUtc: string;
    updatedUtc: string;
    startUtcStr: string;
    endUtcStr: string;
    dueUtcStr: string;
}